import { useState } from 'react'
import JSZip from 'jszip'
import type { DataBundle, DraftRecord, HistoryEntry, ImageRecord } from '../types'
import { sections } from '../data/schemas'
import { downloadBlob, toCsv } from '../utils/csv'
import {
  getImageBlob,
  listDrafts,
  listLocalHistory,
  listLocalImages,
  replaceDrafts,
  replaceLocalHistory,
  replaceLocalImages,
  saveImageBlob,
} from '../utils/storage'

export function Backups({ data }: { data: DataBundle }) {
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  async function createBackup(): Promise<void> {
    setBusy(true)
    try {
      const zip = new JSZip()
      zip.file('Estudiantes.csv', toCsv(sections.find((section) => section.id === 'students')!.headers, data.students))
      zip.file('Sesiones.csv', toCsv(sections.find((section) => section.id === 'sessions')!.headers, data.sessions))
      sections.filter((section) => section.fileName).forEach((section) => {
        zip.file(`contenido/${section.fileName}`, toCsv(section.headers, data.files[section.fileName || ''] ?? []))
      })
      const drafts = listDrafts()
      const history = [...data.history, ...listLocalHistory()]
      const imageMap = new Map<string, ImageRecord>()
      ;[...data.images, ...listLocalImages()].forEach((image) => imageMap.set(image.id, image))
      const images = [...imageMap.values()]
      zip.file('gestor/borradores.json', JSON.stringify(drafts, null, 2))
      zip.file('gestor/historial.json', JSON.stringify(history, null, 2))
      zip.file('gestor/biblioteca-imagenes.json', JSON.stringify(images, null, 2))
      for (const image of images) {
        const blob = await getImageBlob(image.id)
        if (blob) zip.file(`imagenes/${image.fileName}`, blob)
      }
      zip.file('LEEME.txt', 'Respaldo del Gestor Pedagógico de Historia. Contiene CSV compatibles, borradores, historial, biblioteca y archivos WebP disponibles en este navegador. No publiques este ZIP en un repositorio público.')
      downloadBlob(`respaldo-gestor-${new Date().toISOString().slice(0, 10)}.zip`, await zip.generateAsync({ type: 'blob' }))
      setMessage('Respaldo completo descargado.')
    } catch {
      setMessage('No fue posible generar el respaldo.')
    } finally {
      setBusy(false)
    }
  }

  async function restoreBackup(file: File): Promise<void> {
    if (!confirm('La restauración reemplazará borradores, historial local y biblioteca local de imágenes de este navegador. No modifica Google Sheets. ¿Continuar?')) return
    setBusy(true)
    try {
      const zip = await JSZip.loadAsync(file)
      const draftsEntry = zip.file('gestor/borradores.json')
      const historyEntry = zip.file('gestor/historial.json')
      const imagesEntry = zip.file('gestor/biblioteca-imagenes.json')
      if (!draftsEntry || !historyEntry || !imagesEntry) throw new Error('El ZIP no corresponde a un respaldo completo del Gestor V2.')
      const drafts = JSON.parse(await draftsEntry.async('string')) as DraftRecord[]
      const history = JSON.parse(await historyEntry.async('string')) as HistoryEntry[]
      const images = JSON.parse(await imagesEntry.async('string')) as ImageRecord[]
      replaceDrafts(drafts)
      replaceLocalHistory(history)
      replaceLocalImages(images)
      for (const image of images) {
        const imageEntry = zip.file(`imagenes/${image.fileName}`)
        if (imageEntry) await saveImageBlob(image.id, await imageEntry.async('blob'))
      }
      setMessage('Información local restaurada. Recarga el Gestor para actualizar los contadores.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No fue posible restaurar el respaldo.')
    } finally {
      setBusy(false)
    }
  }

  return <div className="workspace">
    <header className="page-head"><p className="eyebrow">Respaldos</p><h1>Proteger antes de publicar una unidad</h1><p>El Gestor recuerda respaldar antes de publicar una unidad nueva. También puedes hacerlo cuando lo necesites.</p></header>
    {message && <div className="alert info">{message}</div>}
    <div className="backup-card"><h2>Respaldo completo</h2><p>Incluye todos los CSV, configuración, borradores, historial de versiones, biblioteca de imágenes y los WebP que todavía estén guardados en este navegador.</p><button className="primary" disabled={busy} onClick={createBackup}>{busy ? 'Preparando…' : 'Descargar respaldo ZIP'}</button></div>
    <div className="backup-card"><h2>Restaurar información local</h2><p>Recupera borradores, historial e imágenes locales. Por seguridad, no sobrescribe Google Sheets automáticamente.</p><label className="file-button"><input type="file" accept=".zip,application/zip" disabled={busy} onChange={(event) => { const file = event.target.files?.[0]; if (file) restoreBackup(file); event.target.value = '' }}/>Seleccionar respaldo ZIP</label></div>
    <div className="checklist"><h2>Antes de un cambio importante</h2><label><input type="checkbox"/> Descargar respaldo.</label><label><input type="checkbox"/> Confirmar que no hay sesiones en curso.</label><label><input type="checkbox"/> Revisar que imágenes y fuentes estén publicadas.</label><label><input type="checkbox"/> Confirmar el estado de la nueva unidad.</label></div>
  </div>
}
